import React, { useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { deleteVenueOrganizer } from "../../../api";
import { images } from "../../../assets/images";

type OrganizerListCardProps = {
  name: string;
  email: string;
  phone: string;
  status: string;
  organizerId: number;
  profilePic: string;
  onDelete?: (organizerId: number) => void;
  venueOrganizerData: any;
};

/** Status badge colors for dark glass theme */
const getStatusStyling = (status: string) => {
  switch ((status || "").toLowerCase()) {
    case "inactive":
      return {
        backgroundColor: "rgba(245, 158, 11, 0.15)",
        borderColor: "rgba(245, 158, 11, 0.45)",
        color: "#fbbf24",
        icon: "fa-solid fa-edit",
      };
    case "active":
      return {
        backgroundColor: "rgba(16, 185, 129, 0.18)",
        borderColor: "rgba(16, 185, 129, 0.45)",
        color: "#34d399",
        icon: "fa-solid fa-circle-check",
      };
    default:
      return {
        backgroundColor: "rgba(255, 255, 255, 0.06)",
        borderColor: "rgba(255, 255, 255, 0.12)",
        color: "rgba(255, 255, 255, 0.7)",
        icon: "fa-solid fa-circle",
      };
  }
};

/**
 * Compact dashboard organizer row.
 * Avatar + name/status/contact on the left; Manage / Edit / Delete in one horizontal row.
 */
const OrganizerListCard = (props: OrganizerListCardProps) => {
  const { name, email, phone, status, organizerId, profilePic } = props;
  const styling = getStatusStyling(status || "active");
  const [modal, setModal] = useState(false);
  const toggleModal = () => setModal(!modal);
  const navigate = useNavigate();

  const handleDelete = async () => {
    try {
      await deleteVenueOrganizer(organizerId);
      toast.success("Organizer Deleted successfully");
      toggleModal();
      window.location.reload();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete organizer";
      toast.error(errorMessage);
    }
  };

  const navigateEventPage = () => {
    localStorage.setItem(
      "venueOrganizerData",
      JSON.stringify(props.venueOrganizerData)
    );
    navigate(`/organizer/${organizerId}`);
  };

  const navigateEditProfile = (e: React.MouseEvent) => {
    e.stopPropagation();
    localStorage.setItem(
      "venueOrganizerData",
      JSON.stringify(props.venueOrganizerData)
    );
    navigate(`/organizer-profile-edit/${organizerId}`);
  };

  const goManage = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigateEventPage();
  };

  return (
    <div
      className="ex-org-row"
      onClick={navigateEventPage}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigateEventPage();
        }
      }}
    >
      <div className="ex-org-row-avatar">
        <img
          src={profilePic || images.profile}
          alt={name}
        />
      </div>

      <div className="ex-org-row-body">
        <div className="ex-org-row-title">
          <h5>{name}</h5>
          <span
            className="ex-status-badge"
            style={{
              backgroundColor: styling.backgroundColor,
              color: styling.color,
              border: `1px solid ${styling.borderColor}`,
            }}
          >
            <i className={styling.icon}></i>
            {status || "active"}
          </span>
        </div>
        <p className="ex-org-row-contact">
          {email}
          {phone ? ` · ${phone}` : ""}
        </p>
      </div>

      <div className="ex-org-row-actions">
        <button className="ex-org-action" type="button" onClick={goManage} title="Manage">
          <i className="fa-solid fa-gear"></i>
          <span>Manage</span>
        </button>
        <button
          className="ex-org-action"
          type="button"
          onClick={navigateEditProfile}
          title="Edit"
        >
          <i className="fa-solid fa-edit"></i>
          <span>Edit</span>
        </button>
        <button
          className="ex-org-action ex-org-action-danger"
          type="button"
          title="Delete"
          onClick={(e) => {
            e.stopPropagation();
            toggleModal();
          }}
        >
          <i className="fa-solid fa-trash"></i>
          <span>Delete</span>
        </button>
      </div>

      <Modal isOpen={modal} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>Delete Organizer</ModalHeader>
        <ModalBody className="text-center text-danger">
          Are you sure you want to delete the organizer <br />
          <strong>{name}</strong>?
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
          >
            Delete
          </Button>
          <Button
            color="secondary"
            onClick={(e) => {
              e.stopPropagation();
              toggleModal();
            }}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <ToastContainer />
    </div>
  );
};

export default OrganizerListCard;
